import { inject, Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { SitemapEntry, SystemEntity } from '@wdx/clmi/clmi-swagger-gen';
import {
    RouteFacade,
    RouterStateAndUrl,
} from '@wdx/shared/infrastructure/state';
import {
    lowerCaseSystemEntityMap,
    PageType,
    ROUTE_PREFIX,
    ROUTE_PREFIX_ADMINISTRATION,
    TenantsService,
    ViewStyle,
} from '@wdx/shared/utils';
import * as R from 'ramda';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    map,
    Observable,
    pairwise,
    startWith,
    take,
} from 'rxjs';
import { NavigationMenu } from '../../../models/navigation-menu.model';

export interface RouterConfig {
    configs: SitemapEntry[];
    isAdminArea: boolean;
    pathPrefix: string;
    path: string[];
    pathWithoutPrefix: string[];
    data: Data;
    wholeSitemap: SitemapEntry[];
}

@Injectable({
    providedIn: 'root',
})
export class NavigationService {
    private routeFacade = inject(RouteFacade);
    private tenantsService = inject(TenantsService);

    private _navMenu: NavigationMenu[];

    private _siteMap: BehaviorSubject<SitemapEntry[]> = new BehaviorSubject(
        undefined,
    );

    public siteMap$ = this._siteMap.asObservable();

    private _adminSiteMap: BehaviorSubject<SitemapEntry[]> =
        new BehaviorSubject(undefined);

    public adminSiteMap$ = this._adminSiteMap.asObservable();

    private _adminNavMenu: BehaviorSubject<NavigationMenu[]> =
        new BehaviorSubject(undefined);

    public adminNavMenu$ = this._adminNavMenu.asObservable();

    public get navMenu(): NavigationMenu[] {
        return this._navMenu;
    }

    public debugDynamicPagesRouting =
        localStorage.getItem('wdx-debug-dynamic-pages-routing') === 'true';

    /**
     * Gets the current sitemap synchronously.
     * Returns undefined if sitemap has not been loaded yet.
     */
    public getSiteMap(): SitemapEntry[] | undefined {
        return this._siteMap.getValue();
    }

    /**
     * Gets the current admin sitemap synchronously.
     * Returns undefined if admin sitemap has not been loaded yet.
     */
    public getAdminSiteMap(): SitemapEntry[] | undefined {
        return this._adminSiteMap.getValue();
    }

    public getRouteForEntityDetailPage(
        entityType: SystemEntity,
        entityId?: string,
    ): string[] | undefined {
        const siteMap = this.getSiteMap();
        const listInRoot = siteMap?.find(
            (entry) => entry.entityType === entityType && entry.hasDetailPage,
        );
        if (listInRoot) {
            return [
                '/',
                this.tenantsService.tenantCode,
                listInRoot.code,
                ...(entityId ? [entityId] : []),
            ];
        }
        const listInChild = siteMap
            ?.flatMap(({ children, code }) =>
                children?.map((y) => ({ ...y, parent: code })),
            )
            .filter(Boolean)
            .find(
                (entry) =>
                    entry.entityType === entityType && entry.hasDetailPage,
            );
        if (listInChild) {
            return [
                '/',
                this.tenantsService.tenantCode,
                listInChild.parent,
                listInChild.code,
                ...(entityId ? [entityId] : []),
            ];
        }
        return undefined;
    }

    public setSiteMap(siteMap: SitemapEntry[]): void {
        this._siteMap.next(siteMap);
        this._navMenu = siteMap.map((menuItem) =>
            this.createNavigationMenu(menuItem),
        );
    }

    public setAdminSiteMap(adminSiteMap: SitemapEntry[]): void {
        this._adminSiteMap.next(adminSiteMap);
        this._adminNavMenu.next(
            adminSiteMap.map((menuItem) =>
                this.createNavigationMenu(
                    menuItem,
                    null,
                    ROUTE_PREFIX_ADMINISTRATION[0],
                ),
            ),
        );
    }

    public getCurrentConfig$(): Observable<RouterConfig> {
        return combineLatest({
            adminSiteMap: this.adminSiteMap$,
            siteMap: this.siteMap$,
            routeState: this.routeFacade.getRouteState$(),
        }).pipe(
            startWith({
                adminSiteMap: [] as SitemapEntry[],
                siteMap: [] as SitemapEntry[],
                routeState: { data: {}, path: [] } as RouterStateAndUrl,
            }),
            map(({ adminSiteMap, siteMap, routeState }) => {
                const { path, data } = routeState;
                const isAdminArea = this.isAdminArea(path);
                const pathPrefix = isAdminArea
                    ? `/${path[0]}/${ROUTE_PREFIX_ADMINISTRATION[0]}`
                    : `/${path[0]}`;
                const pathParts = isAdminArea ? path.slice(2) : path.slice(1);
                const currentSiteMap = isAdminArea ? adminSiteMap : siteMap;
                const currentConfig: SitemapEntry[] = [];
                if (currentSiteMap) {
                    pathParts.forEach((part, index) => {
                        if (index === 0) {
                            currentConfig.push(
                                currentSiteMap.find(
                                    (entry) => entry.code === part,
                                ),
                            );
                        } else {
                            currentConfig.push(
                                currentConfig[index - 1]?.children?.find(
                                    (entry) => entry.code === part,
                                ),
                            );
                        }
                    });
                }
                return {
                    configs: currentConfig,
                    isAdminArea,
                    pathPrefix,
                    path,
                    pathWithoutPrefix: pathParts,
                    data,
                    wholeSitemap: currentSiteMap,
                };
            }),
            pairwise(),
            filter(([prev, current]) => !R.equals(prev, current)),
            map(([_, current]) => R.clone(current)),
        );
    }

    public getListPageConfig$(): Observable<{
        config: SitemapEntry;
        path: string[];
    }> {
        return this.getCurrentConfig$().pipe(
            map(({ configs, path }) => {
                if (
                    configs.includes(undefined) &&
                    path.length > 2 &&
                    path[1] === 'query' &&
                    path[2].length > 2
                ) {
                    const entityType: SystemEntity =
                        lowerCaseSystemEntityMap()[path[2]] ??
                        (path[2] as SystemEntity);
                    return {
                        config: {
                            entityType,
                        },
                        path,
                    };
                }
                const config = R.last(
                    configs.filter(
                        (config) =>
                            config?.pageType === PageType.List ||
                            config?.supportedViewStyles?.includes(
                                ViewStyle.List,
                            ),
                    ),
                );
                return R.clone({
                    config,
                    path,
                });
            }),
        );
    }

    public isAdminArea$() {
        return this.routeFacade.getPath$().pipe(
            take(1),
            map((path) => this.isAdminArea(path)),
        );
    }

    public isAdminArea(path: string[]): boolean {
        return path[1] === ROUTE_PREFIX_ADMINISTRATION[0];
    }

    private createNavigationMenu(
        menuItem: SitemapEntry,
        parentItem?: SitemapEntry,
        basePath?: string,
    ): NavigationMenu {
        const linkPrefix = basePath
            ? [...ROUTE_PREFIX, basePath]
            : [...ROUTE_PREFIX];

        return {
            id: menuItem.code,
            pageType: menuItem.pageType,
            translationKey: menuItem?.displayName?.key,
            ...(menuItem?.icon ? { icon: menuItem?.icon } : {}),
            routerLink: parentItem
                ? [...linkPrefix, parentItem.code, menuItem.code]
                : [...linkPrefix, menuItem.code],
            cySelector: menuItem.code,
            disabledBy: [],
            ...(menuItem?.children?.length
                ? {
                      menu: menuItem?.children.map((child) =>
                          this.createNavigationMenu(child, menuItem, basePath),
                      ),
                  }
                : {}),
        };
    }
}
